import React, { forwardRef , useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { FieldArray, FieldArrayRenderProps, FormikContextType, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { FullScreenDialog } from '../../../../../../ui-kit';
import { DeliveryListFormValues, DeliveryListItemFormValue, StockCard } from '../../types';
import { usePriceService } from '../../../../../../utils/hooks/priceService';
import { useUser } from '../../../../../../utils/hooks/auth';
import { useWarehousesScope } from '../../../../../warehouse/providers/WarehouseScopeProvider';
import DeliveryListCreateWarehouseForm from '../../../DeliveryListCreateWarehouseForm';
import { CreateWarehouseCardMutation } from '../../../../../../generated/graphql';
import { StockCardSearch, StockCardSearchMobile } from '../common/StockCardSearch';
import { PickedCount } from '../common/PickedCount';
import { DELIVERY_LIST_ITEMS_FIELD } from '../BasicInformation/constants';
import { PickedStockCardsContainer } from './style';
import { DeliveryItem } from './DeliveryItem';

const PickIngredients = forwardRef<HTMLDivElement>((_, ref) => {
    const { t } = useTranslation();
    const [isStockCardSearchOpened, setIsStockCardSearchOpened] = useState(false);
    const [passedSearchValue, setPassedSearchValue] = useState<string | undefined>(undefined);
    const openMobileSearchPopup = () => {
        if(!isDesktop){
            setIsStockCardSearchOpened(true);
        }
    };

    const onMobileSearchDialogClose = () => {
        setIsStockCardSearchOpened(false);
    };
    const [isCreateWarehouseCardOpen, setIsCreateWarehouseCardOpen] = useState(false);
    const openCreateWarehouseCardDialog = () => setIsCreateWarehouseCardOpen(true);
    const closeCreateWarehouseCardDialog = () => setIsCreateWarehouseCardOpen(false);

    const { currentWarehouseId } = useWarehousesScope();
    const { priceService } = usePriceService();
    const { user: { defaultCurrency: { currencyCode } } } = useUser();
    const formikContext = useFormikContext<DeliveryListFormValues>();
    const { setFormikState, values, setFieldValue } = formikContext;

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const onAdd = (stockCard: StockCard) => {
        const { stockLevels, name, id: stockCardId, storageUnit, } = stockCard;

        const stockLevel = stockLevels.find(({ warehouseId }) => warehouseId === currentWarehouseId) as StockCard['stockLevels'][0];

        const priceBuy = stockLevel.priceWithoutVat;

        setFormikState(formikState => {
            const deliveryListItems = [
                ...values.deliveryListItems,
                {
                    quantity: 1,
                    priceWithoutVat: priceBuy ? priceService.toUnit(priceBuy, currencyCode) : 0,
                    stockCardId,
                    name,
                    storageUnit,
                    totalCost: priceBuy ? priceService.toUnit(priceBuy, currencyCode) : 0,
                }
            ];

            return {
                ...formikState,
                values: {
                    ...values,
                    deliveryListItems,
                },
            };
        });
    };

    const onRemove = (stockCardId: string) => {
        setFormikState(formikState => {
            const deliveryListItems = formikState.values.deliveryListItems.filter(item => item.stockCardId !== stockCardId);

            return {
                ...formikState,
                values: {
                    ...formikState.values,
                    deliveryListItems,
                },
            };
        });
    };

    const onWarehouseCardCreateCreate = ({ createStockCard }: CreateWarehouseCardMutation) => {
        setPassedSearchValue(createStockCard.name);
        const { deliveryListItems } = values;
        const createdOption: DeliveryListItemFormValue = {
            quantity: 1,
            priceWithoutVat: 0,
            stockCardId: createStockCard.id,
            name: createStockCard.name,
            storageUnit: createStockCard.storageUnit,
            totalCost: 0,
        };
        setFieldValue(DELIVERY_LIST_ITEMS_FIELD,[createdOption,...deliveryListItems]);
        closeCreateWarehouseCardDialog();
    };


    return (
        <>
            <Grid container alignItems='flex-end' spacing={2} ref={ref}>
                <Grid item xs={6}>
                    <StockCardSearch
                        onCreateNewClick={openCreateWarehouseCardDialog}
                        pickedStockCards={values.deliveryListItems}
                        onAdd={onAdd}
                        passedSearchValue={passedSearchValue}
                        openMobilePopup={isDesktop ? undefined : openMobileSearchPopup}
                    />
                </Grid>
                <Grid item xs={6}>
                    <PickedCount count={formikContext.values.deliveryListItems.length} />
                </Grid>
                <PickedStockCardsContainer>
                    <FieldArray
                        name='deliveryListItems'
                        render={({ form, remove }: FieldArrayRenderProps) => (form as FormikContextType<DeliveryListFormValues>)
                            .values.deliveryListItems.map((stockCard, index) => (
                                <DeliveryItem handleRemove={remove} index={index} key={stockCard.stockCardId} stockCard={stockCard} />
                            ))}
                    />
                </PickedStockCardsContainer>
            </Grid>
            <FullScreenDialog isOpened={isStockCardSearchOpened}>
                <StockCardSearchMobile
                    onCreateNewClick={openCreateWarehouseCardDialog}
                    pickedStockCards={values.deliveryListItems}
                    passedSearchValue={passedSearchValue}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    onClose={onMobileSearchDialogClose}
                />
            </FullScreenDialog>
            <FullScreenDialog isOpened={isCreateWarehouseCardOpen}>
                <DeliveryListCreateWarehouseForm
                    dialogName={t('New warehouse card')}
                    onClose={closeCreateWarehouseCardDialog}
                    onMutationFinished={onWarehouseCardCreateCreate}
                    requiredWarehouseIds={[currentWarehouseId as string]}
                />
            </FullScreenDialog>
        </>
    );
});

export default PickIngredients;
